// Tipos do Admin do RCS (task 17tm466frtx), no formato do contrato
// `RCS-CONTRACT.md` (seção "API", bloco Admin). Ficam aqui, e não em
// `src/app/models/`, porque a frente do cliente cria os tipos dela em paralelo:
// dois arquivos com o mesmo nome em `models/` seriam conflito certo no merge.
// Quando as duas frentes estiverem juntas, o que for comum (estágio, caso de
// uso) pode subir para `models/Rcs.ts`.

/** Canal de um grupo de fracionamento ou de uma conta de provedor. */
export type RcsChannel = 'sms' | 'rcs';

/** Etapas do agente, na ordem em que a fila anda. */
export type RcsAgentStage =
  | 'submitted'
  | 'technical_review'
  | 'carrier_review'
  | 'active'
  | 'rejected'
  | 'suspended';

export type RcsUseCase = 'transactional' | 'promotional' | 'otp';

export interface RcsProviderAccountRef {
  id: string;
  name: string;
}

/** Uma marca registrada num broker: o `sender_id` que o broker criou para ela. */
export interface RcsAgentSender {
  provider_account: RcsProviderAccountRef;
  sender_id: string;
  status: 'active' | 'inactive';
}

export interface RcsAgentHistoryEntry {
  stage: RcsAgentStage;
  at: string;
  note?: string | null;
}

/** Checagens que o backend já fez sobre a ficha (seção 4 da spec). */
export interface RcsAgentChecks {
  logo: boolean;
  banner: boolean;
  domains: boolean;
}

/** Agente como a antiga Fila do Admin recebia: `GET /api/admin/rcs/agents` (a Fila entrou no Acompanhamento em 29/10). */
export interface RcsQueueAgent {
  id: string;
  name: string;
  color?: string | null;
  cnpj?: string | null;
  owner_name?: string | null;
  owner_email?: string | null;
  use_case: RcsUseCase;
  description?: string | null;
  logo_url?: string | null;
  banner_url?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  terms_url?: string | null;
  privacy_url?: string | null;
  stage: RcsAgentStage;
  rejection_reason?: string | null;
  submitted_at?: string | null;
  approved_at?: string | null;
  history?: RcsAgentHistoryEntry[];
  user: { id: string; name: string; email: string };
  days_in_stage: number;
  checks: RcsAgentChecks;
  senders: RcsAgentSender[];
}

// --- Métricas ------------------------------------------------------------------

/**
 * `GET /api/admin/rcs/metrics?month=YYYY-MM`.
 *
 * O contrato fixa só as chaves de topo. As linhas das três tabelas seguem as
 * colunas do protótipo; se o backend devolver outro nome, é aqui que se ajusta.
 * Taxas vêm como fração (0 a 1), como o resto do painel devolve proporção.
 */
export interface RcsMetrics {
  /** Retorno do broker (entregue/lido) ligado? `false` desde 02/11/2026 (`RCS_PROVIDER_WEBHOOKS_ENABLED`). */
  provider_status?: boolean;
  cache_hit_rate: number;
  new_lookups: number;
  expensive_errors: number;
  fallback_rate: number;
  by_client: RcsMetricsClientRow[];
  by_broker: RcsMetricsBrokerRow[];
  reconciliation: RcsReconciliationRow[];
  /** Condição "se não ler em X h, enviar SMS" (F7.5), pelo prazo que venceu no mês. */
  read_condition?: RcsReadConditionMetrics | null;
}

/** Bloco `read_condition` das métricas (só `controls_rcs`). Custo em micros de real. */
export interface RcsReadConditionMetrics {
  /** Flag `RCS_SEQUENCE_READ_CONDITION`. Desligada e zerado, a tela esconde o bloco. */
  enabled: boolean;
  steps: number;
  scheduled: number;
  read_in_time: number;
  sms_sent: number;
  sms_delivered: number;
  sms_undelivered: number;
  sms_delivered_rate: number;
  sms_clicked: number;
  /** O SMS não saiu (sem crédito/saldo ou erro no meio). */
  failed: number;
  skipped: number;
  pending: number;
  sms_cost_micros: number;
  /** SMS cobrados do pacote de créditos (1 crédito cada, sem valor em real). */
  sms_package: number;
}

export interface RcsMetricsClientRow {
  user_id?: string;
  user_name: string;
  messages: number;
  fallback_rate: number;
  new_lookups: number;
  expensive_errors: number;
}

export interface RcsMetricsBrokerRow {
  provider_account_id?: string;
  provider_account_name: string;
  sent: number;
  delivered_rate: number;
  read_rate: number;
  refused_rate: number;
}

export interface RcsReconciliationRow {
  date: string;
  log_count: number;
  invoice_count: number | null;
  difference: number | null;
  /** `ok` bate; `alert` já alertou; `pending` a fatura do dia não chegou. */
  status: 'ok' | 'alert' | 'pending';
}

// --- Prévia de rota ------------------------------------------------------------

export type RcsOrigin = 'broadcast' | 'automation';

export interface RcsRouteShare {
  provider_account: RcsProviderAccountRef;
  percent: number;
}

/** Por operadora: a divisão entre brokers, ou `blocked` quando não sobra conta ativa. */
export type RcsCarrierRoute = RcsRouteShare[] | 'blocked';

/**
 * `GET /api/admin/rcs/route-preview?user_id=`: uma linha por agente ativo e
 * origem, com a divisão por operadora (Vivo, Claro, TIM).
 */
export interface RcsRoutePreviewRow {
  agent: { id: string; name: string; color?: string | null };
  origin: RcsOrigin;
  carriers: { [carrier: string]: RcsCarrierRoute };
}

// --- Usuário -------------------------------------------------------------------

/**
 * Campos que o contrato acrescenta ao usuário. Ficam fora de `models/User.ts`
 * pelo mesmo motivo do topo do arquivo; a tela de edição lê e grava por aqui.
 */
export interface RcsUserFields {
  rcs_enabled?: boolean;
  broadcast_rcs_fractionation_route?: string | null;
  automation_rcs_fractionation_route?: string | null;
}

// --- Rótulos e regras de tela ---------------------------------------------------

/** O que o admin lê em cada etapa (textos do protótipo). */
export const RCS_STAGE_LABELS: Record<RcsAgentStage, string> = {
  submitted: 'Recebido',
  technical_review: 'Em análise pela SMS Funnel',
  carrier_review: 'No broker e operadoras',
  active: 'Ativo',
  rejected: 'Reprovado',
  suspended: 'Suspenso',
};

export const RCS_USE_CASE_LABELS: Record<RcsUseCase, string> = {
  transactional: 'Transacional',
  promotional: 'Promocional',
  otp: 'Código de acesso',
};

/**
 * Ordem da fila: primeiro o que depende do admin, depois o que está parado,
 * e por fim o que já acabou. É a ordem do protótipo.
 */
export const RCS_QUEUE_ORDER: RcsAgentStage[] = [
  'submitted',
  'technical_review',
  'carrier_review',
  'suspended',
  'active',
  'rejected',
];

/** Etapas em que o agente espera alguém agir; só nelas "parado há N dias" faz sentido. */
export const RCS_PENDING_STAGES: RcsAgentStage[] = ['submitted', 'technical_review', 'carrier_review'];

/** Acima disto o card ganha o alerta de parado: é aí que o prazo de lançamento escorrega. */
export const RCS_STALLED_AFTER_DAYS = 5;

export function isStalled(agent: Pick<RcsQueueAgent, 'stage' | 'days_in_stage'>): boolean {
  return RCS_PENDING_STAGES.includes(agent.stage) && Number(agent.days_in_stage) > RCS_STALLED_AFTER_DAYS;
}

/** `sender_id` como o Agent Builder mostra: letras, números e sublinhado. */
export const RCS_SENDER_ID_PATTERN = /^[A-Za-z0-9_]{4,}$/;

/** Conta de provedor da PingooMobi RCS (`ProviderAccount::PINGOO_RCS` no backend). */
export const PINGOO_RCS_ACCOUNT_ID = '01a0e0e0-5c00-7000-8000-000000000013';

/**
 * Pingoo Omni (21/10): o sender da marca é o `agent_id` UUID do agente na Pingoo
 * (`GET /rcs/agents`, `php artisan rcs:pingoo-agents`), não o id do Google
 * (`sms_funnel_..._agent`), que a Omni recusa em todo envio.
 */
export const RCS_PINGOO_AGENT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** O cliente lê o motivo; menos que isto não explica o que corrigir. */
export const RCS_REJECTION_MIN_LENGTH = 10;

/** Operadoras da prévia de rota, na ordem em que o protótipo mostra. */
export const RCS_CARRIERS = ['Vivo', 'Claro', 'TIM'];

/**
 * Aceita lista crua ou envelopada em `data`. O contrato não fixa o envelope
 * das rotas novas, e o painel tem os dois estilos (o BFF da carteira envelopa,
 * os controllers antigos não).
 */
export function unwrapList<T>(response: T[] | { data?: T[] } | null | undefined): T[] {
  if (Array.isArray(response)) {
    return response;
  }

  return (response && Array.isArray(response.data)) ? response.data : [];
}

// --- Acompanhamento de agentes (tela nova, 17tm466frtx) -------------------------
//
// `GET /admin/rcs/tracking` e `GET /admin/rcs/tracking/{id}`, como o `RCS-ACOMP-CONTRACT.md`
// descreve. É uma etapa mais fina que `RcsAgentStage` (que continua sendo a trava de envio):
// a Pingoo manda o estado de verificação da marca e de lançamento por operadora, e o backend
// deriva `progress_step`/`progress_detail` disso. Tipos à parte de `RcsQueueAgent` porque a
// fila (`/admin/rcs/agents`) e o acompanhamento (`/admin/rcs/tracking`) são rotas e shapes
// diferentes, ainda que descrevam o mesmo agente.

/** As 5 etapas finas que a Pingoo mostra, na ordem em que o agente anda. */
export type RcsProgressStep = 'submitted' | 'brand_verification' | 'google_review' | 'carrier_launch' | 'active';

export type RcsProgressDetail =
  | 'awaiting_review'
  | 'awaiting_provider_link'
  | 'awaiting_verification_request'
  | 'awaiting_owner'
  | 'owner_confirmed'
  | 'verified'
  | 'carriers_pending'
  | 'launched_partial'
  | 'launched_all'
  | 'provider_error'
  // Criação automática na Pingoo (17tm466v1y7): só a equipe vê; o cliente lê "em análise".
  | 'pingoo_creating'
  | 'pingoo_create_failed';

export const RCS_PROGRESS_STEP_LABELS: Record<RcsProgressStep, string> = {
  submitted: '1 · Enviado',
  brand_verification: '2 · Verificação da marca',
  google_review: '3 · Análise do Google',
  carrier_launch: '4 · Lançamento nas operadoras',
  active: '5 · Ativo',
};

/** Rótulo curto (sem o número), para a pílula da tabela e do painel lateral. */
export const RCS_PROGRESS_STEP_SHORT_LABELS: Record<RcsProgressStep, string> = {
  submitted: 'Enviado · conferência',
  brand_verification: 'Aguardando o cliente',
  google_review: 'Análise do Google',
  carrier_launch: 'Lançamento nas operadoras',
  active: 'Ativo',
};

export type RcsCarrierState = 'launched' | 'pending' | 'rejected' | 'suspended' | 'unlaunched' | 'unknown';

/** TIM, Vivo, Claro, Oi — nessa ordem fixa, como as telas sempre mostram (contrato). */
export const RCS_CARRIER_ORDER = ['tim', 'vivo', 'claro', 'oi'];

export const RCS_CARRIER_LABELS: Record<string, string> = {
  tim: 'TIM',
  vivo: 'Vivo',
  claro: 'Claro',
  oi: 'Oi',
};

export interface RcsTrackingCarrier {
  carrier: string;
  label: string;
  state: RcsCarrierState;
  comment?: string | null;
  updated_at?: string | null;
}

export interface RcsAccountManager {
  id: string;
  name: string;
  email?: string;
}

export interface RcsTrackingClient {
  id: string;
  name: string;
  email: string;
  cnpj?: string | null;
  account_manager?: RcsAccountManager | null;
}

export interface RcsTrackingLastContact {
  channel: string;
  event: string;
  at: string;
  by: 'auto' | 'team' | 'customer';
}

/** Uma linha da tabela — `data[]` ou dentro de `groups[].agents[]`. */
export interface RcsTrackingRow {
  id: string;
  name: string;
  color?: string | null;
  logo_url?: string | null;
  use_case: RcsUseCase;
  stage: RcsAgentStage;
  progress_step: RcsProgressStep;
  progress_detail?: RcsProgressDetail | null;
  progress_label?: string | null;
  days_in_step: number;
  step_changed_at?: string | null;
  awaiting_customer_since?: string | null;
  submitted_at?: string | null;
  approved_at?: string | null;
  carriers: RcsTrackingCarrier[];
  client: RcsTrackingClient;
  last_contact?: RcsTrackingLastContact | null;
  notifications_count?: number;
  pingoo_agent_id?: string | null;
  google_agent_id?: string | null;
  /** Era a Fila de agentes (29/10): ficha recebida, em análise ou no broker sem a Pingoo associada. */
  needs_action?: boolean;
  /** Criação automática na Pingoo e vínculo (subtask 17tm466v1y7). */
  pingoo_create?: RcsPingooCreate | null;
}

/**
 * Criação do agente na Pingoo pelo sistema (subtask 17tm466v1y7). `status` nulo = fluxo de
 * antes (UUID colado à mão); `linked` = agente criado fora e vinculado no Acompanhamento.
 */
export type RcsPingooCreateStatus = 'queued' | 'creating' | 'created' | 'failed' | 'linked';

export interface RcsPingooCreate {
  /** `RCS_PINGOO_AUTO_CREATE` ligada no backend. */
  enabled: boolean;
  status: RcsPingooCreateStatus | null;
  error?: string | null;
  attempts?: number;
  requested_at?: string | null;
  created_at?: string | null;
  brand_id?: string | null;
  has_document?: boolean;
  document_url?: string | null;
  can_create: boolean;
  can_retry: boolean;
  can_link: boolean;
}

/** Agente da conta Pingoo ainda sem dono aqui (`GET /admin/rcs/agents/{id}/pingoo/candidates`). */
export interface RcsPingooCandidate {
  id: string;
  name: string;
  status?: string | null;
  brand_id?: string | null;
  brand_name?: string | null;
  logo_url?: string | null;
  created_at?: string | null;
  /** Bruto da Pingoo (`VERIFICATION_STATE_*`), ou nulo sem retrato. */
  verification?: string | null;
  carriers: { carrier: string; label: string; state: RcsCarrierState | string }[];
}

export interface RcsPingooCandidates {
  candidates: RcsPingooCandidate[];
  /** O único candidato com o mesmo nome (ou a mesma marca), pré-selecionado. */
  suggested_id: string | null;
  error: string | null;
}

/** `VERIFICATION_STATE_*` da Pingoo em pt-BR (lista do "Vincular"). */
export const RCS_PINGOO_VERIFICATION_LABELS: Record<string, string> = {
  VERIFICATION_STATE_VERIFIED: 'Marca verificada',
  VERIFICATION_STATE_PENDING: 'Verificação pendente',
  VERIFICATION_STATE_UNVERIFIED: 'Verificação não pedida',
};

export interface RcsTrackingGroup {
  client: RcsTrackingClient;
  agents: RcsTrackingRow[];
  counts?: { [key: string]: number };
}

export interface RcsTrackingKpis {
  in_review: number;
  awaiting_customer: { agents: number; clients: number };
  /** `days` é o `stuck_days` que o backend usou para contar (o pedido ou o padrão da config). */
  stuck: { count: number; days: number };
  active: number;
  rejected_suspended: number;
  /** "Precisa de ação técnica" (o que a Fila de agentes mostrava). */
  needs_action?: number;
}

export interface RcsTrackingMeta {
  page: number;
  per_page: number;
  last_page: number;
}

/** `GET /admin/rcs/tracking`. */
export interface RcsTrackingResponse {
  kpis: RcsTrackingKpis;
  total: number;
  clients_count: number;
  provider_checked_at?: string | null;
  data: RcsTrackingRow[];
  /** Só vem com `group=client`. */
  groups?: RcsTrackingGroup[];
  meta: RcsTrackingMeta;
  account_managers: RcsAccountManager[];
  stuck_days: number;
}

export interface RcsTrackingStepInfo {
  key: string;
  label: string;
  state: 'done' | 'current' | 'waiting' | 'todo' | 'failed';
  at?: string | null;
  note?: string | null;
}

export interface RcsTrackingHistoryEntry extends RcsAgentHistoryEntry {
  step?: string | null;
  event?: string | null;
  carrier?: string | null;
}

export interface RcsTrackingNotification {
  id: string;
  event: string;
  channel: 'panel' | 'email' | 'whatsapp' | 'sms' | 'phone' | string;
  to?: string | null;
  status: 'sent' | 'failed' | 'skipped' | 'delivered' | 'read';
  sent_at: string;
  by: 'auto' | 'team' | 'customer';
  text?: string | null;
}

export interface RcsTrackingNote {
  id: string;
  kind: 'note' | 'call';
  body: string;
  author: { id: string; name: string };
  created_at: string;
}

/** `GET /admin/rcs/tracking/{id}`: a linha de cima, mais o que só o painel lateral mostra. */
export interface RcsTrackingDetail extends RcsTrackingRow {
  steps: RcsTrackingStepInfo[];
  history: RcsTrackingHistoryEntry[];
  notifications: RcsTrackingNotification[];
  notes: RcsTrackingNote[];
  verification_contact_email?: string | null;
  owner_name?: string | null;
  owner_email?: string | null;
  eta?: { from: string; to: string } | null;
  provider_raw?: unknown;
  provider_checked_at?: string | null;
  provider_error?: string | null;
  reminders_sent?: number;
  resend_requested_at?: string | null;
  /** Só vem quando o agente não tem sender: nome parecido achado em `GET /rcs/agents` da Pingoo. */
  provider_suggestions?: { id: string; name: string }[];

  // O que a Fila de agentes mostrava (entrou no painel lateral em 29/10).
  description?: string | null;
  banner_url?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  terms_url?: string | null;
  privacy_url?: string | null;
  rejection_reason?: string | null;
  days_in_stage?: number;
  checks?: RcsAgentChecks | null;
  senders?: RcsAgentSender[];
}

/**
 * Filtros de `GET /admin/rcs/tracking`, como o contrato lista. `step` aceita um
 * `RcsProgressStep` OU um dos quatro valores compostos do contrato
 * (`awaiting_customer`, `rejected`, `suspended`, `in_review`).
 *
 * `rejected_suspended` NÃO está no contrato original: é o que o KPI "Reprovados" (que soma
 * `rejected` + `suspended`, igual ao card do protótipo) manda ao clicar. Proposta de aditivo ao
 * contrato (ver relatório) — o backend que não aceitar devolve a lista sem filtrar por ele, o
 * que é inofensivo (o pior caso é mostrar mais linhas que as 2 etapas terminais).
 */
export type RcsTrackingStepFilter = RcsProgressStep | 'awaiting_customer' | 'rejected' | 'suspended' | 'in_review' | 'rejected_suspended'
  | 'needs_action';

export interface RcsTrackingFilters {
  q?: string;
  step?: RcsTrackingStepFilter | '';
  client_id?: string;
  use_case?: RcsUseCase | '';
  carrier?: string;
  carrier_state?: RcsCarrierState;
  /** Presente = também filtra a lista (não só o KPI "Parados"); ausente = o backend usa o padrão da config. */
  stuck_days?: number;
  /** uuid de admin, ou `'none'` para "sem responsável". */
  account_manager_id?: string;
  from?: string;
  to?: string;
  sort?: 'stuck' | 'recent' | 'client' | 'contact';
  group?: 'client';
  page?: number;
  per_page?: number;
}

export const RCS_TRACKING_STEP_OPTIONS: { value: RcsTrackingStepFilter | ''; label: string }[] = [
  { value: '', label: 'Todas' },
  { value: 'needs_action', label: 'Precisa de ação técnica' },
  { value: 'submitted', label: RCS_PROGRESS_STEP_LABELS.submitted },
  { value: 'brand_verification', label: RCS_PROGRESS_STEP_LABELS.brand_verification },
  { value: 'google_review', label: RCS_PROGRESS_STEP_LABELS.google_review },
  { value: 'carrier_launch', label: RCS_PROGRESS_STEP_LABELS.carrier_launch },
  { value: 'active', label: RCS_PROGRESS_STEP_LABELS.active },
  { value: 'awaiting_customer', label: 'Aguardando o cliente' },
  { value: 'in_review', label: 'Em análise (enviado, Google ou operadoras)' },
  { value: 'rejected', label: 'Reprovado' },
  { value: 'suspended', label: 'Suspenso' },
];

export const RCS_TRACKING_SORT_OPTIONS: { value: NonNullable<RcsTrackingFilters['sort']>; label: string }[] = [
  { value: 'stuck', label: 'Mais parado primeiro' },
  { value: 'recent', label: 'Enviado mais recente' },
  { value: 'client', label: 'Cliente (A–Z)' },
  { value: 'contact', label: 'Último contato mais antigo' },
];

/** Opções do seletor "Operadora" — par operadora+estado, ou só o estado (ex.: qualquer uma recusou). */
export const RCS_TRACKING_CARRIER_OPTIONS: { value: string; label: string; carrier?: string; state?: RcsCarrierState }[] = [
  { value: '', label: 'Todas' },
  { value: 'tim:pending', label: 'TIM · em análise', carrier: 'tim', state: 'pending' },
  { value: 'vivo:pending', label: 'Vivo · em análise', carrier: 'vivo', state: 'pending' },
  { value: 'claro:pending', label: 'Claro · em análise', carrier: 'claro', state: 'pending' },
  { value: 'oi:pending', label: 'Oi · em análise', carrier: 'oi', state: 'pending' },
  { value: ':rejected', label: 'Qualquer uma recusou', state: 'rejected' },
  { value: 'tim:launched', label: 'TIM · lançado', carrier: 'tim', state: 'launched' },
  { value: 'vivo:launched', label: 'Vivo · lançado', carrier: 'vivo', state: 'launched' },
  { value: 'claro:launched', label: 'Claro · lançado', carrier: 'claro', state: 'launched' },
  { value: 'oi:launched', label: 'Oi · lançado', carrier: 'oi', state: 'launched' },
];

/** Degraus do "Parado há mais de" — tanto o filtro quanto o seletor embutido no KPI usam esta lista. */
export const RCS_STUCK_DAY_STEPS = [3, 5, 7, 10, 15];

/** Tom do pill de etapa, no padrão `pill--*` do protótipo (viram classe `stage-chip--*`). */
export function trackingStepTone(row: Pick<RcsTrackingRow, 'stage' | 'progress_step'> & { progress_detail?: string | null }): string {
  if (row.stage === 'rejected' || row.stage === 'suspended' || row.progress_detail === 'pingoo_create_failed') {
    return 'bad';
  }

  const tones: Record<RcsProgressStep, string> = {
    submitted: 'muted',
    brand_verification: 'warm',
    google_review: 'cool',
    carrier_launch: 'violet',
    active: 'ok',
  };

  return tones[row.progress_step] || 'muted';
}

/** Rótulo da pílula de etapa: `stage` terminal vence; senão o rótulo curto do `progress_step`. */
export function trackingStepLabel(row: Pick<RcsTrackingRow, 'stage' | 'progress_step'> & { progress_detail?: string | null }): string {
  if (row.stage === 'rejected') {
    return 'Reprovado';
  }
  if (row.stage === 'suspended') {
    return 'Suspenso';
  }
  // Enviado à Pingoo e ainda sem o UUID dela (caso dos agentes criados no formulário da Pingoo):
  // "conferência" diria que a bola está com a SMS Funnel, e não está.
  if (row.progress_detail === 'awaiting_provider_link') {
    return 'Aguardando a Pingoo associar';
  }
  // Criação automática na Pingoo (17tm466v1y7).
  if (row.progress_detail === 'pingoo_create_failed') {
    return 'Erro ao criar na Pingoo';
  }
  if (row.progress_detail === 'pingoo_creating') {
    return 'Criando na Pingoo';
  }

  return RCS_PROGRESS_STEP_SHORT_LABELS[row.progress_step] || row.progress_step;
}

/** "Na etapa há N dias" só faz sentido antes de Ativo (depois disso a tabela mostra "—"). */
export function trackingIsActiveCountdown(row: Pick<RcsTrackingRow, 'stage' | 'progress_step'>): boolean {
  return row.stage !== 'rejected' && row.stage !== 'suspended' && row.progress_step !== 'active';
}

/** A pílula "Aguardando o cliente" leva o envelope antes do texto, como no mockup. */
export function trackingPillIcon(row: Pick<RcsTrackingRow, 'stage' | 'progress_step'>): string | null {
  return trackingIsActiveCountdown(row) && row.progress_step === 'brand_verification' ? 'fa-envelope' : null;
}

/** Status de um aviso enviado, em pt-BR (contrato: `sent|failed|skipped|delivered|read`). */
/** Evento do aviso (`rcs_agent_notifications.event`) em pt-BR, para "Último contato". */
export const RCS_NOTIFICATION_EVENT_LABELS: Record<string, string> = {
  action_required: 'pediu a aprovação',
  reminder: 'lembrete',
  step_changed: 'avisou mudança de etapa',
  carrier_launched: 'operadora lançou',
  carrier_rejected: 'operadora recusou',
  active: 'agente ativo',
  rejected: 'agente reprovado',
  suspended: 'agente suspenso',
  instructions: 'passo a passo',
  resend_requested: 'pedido de reenvio',
  manual: 'contato da equipe',
};

export const RCS_NOTIFICATION_STATUS_LABELS: Record<string, string> = {
  sent: 'ENVIADO',
  delivered: 'ENTREGUE',
  read: 'LIDO',
  failed: 'FALHOU',
  skipped: 'NÃO ENVIADO',
};
